import { useEffect, useState } from "react";
import {
  NPC_ROLES,
  NPC_ROLE_META,
  NPC_AGENT_LIMITS,
  DOCENT_PROVIDERS,
  KB_TAG_LABEL,
  allowedLevels,
  agentFor,
  packSummary,
  type KbPackDoc,
  type NpcAgent,
  type NpcAgentDoc,
  type NpcLevel,
} from "@greg-chick/shared";
import {
  aiProviders,
  draftNpcQa,
  getAiKb,
  getNpcAgents,
  saveNpcAgents,
  translateNpcQa,
} from "../lib/contentApi";
import { LANG_NAME, type AiProvider, type AiProviderOption, type Lang } from "@greg-chick/shared";
import { useRegisterSave } from "../lib/saveBar";
import { NpcChatLogBox } from "./NpcChatLogBox";
import { Msg } from "./ui";
import { NpcChatPreview } from "./NpcChatPreview";

/**
 * 🐱 **이 친구가 무엇을 아는가** — [🧸 캐릭터] 안, 한 친구를 고른 다음 자리.
 *
 * ## 왜 「켜기/끄기」가 아닌가
 * 타깃이 0~7세다. 그 나이 아이는 글을 읽지도 타이핑도 못 한다 — 자유 대화는 「위험해서 막는
 * 것」이 아니라 **아이가 쓸 수 없는 물건**이고, 실제 사용자는 옆에 있는 어른이다.
 * 그래서 가운데 칸(승인된 문답)이 있어야 대부분의 쓸모가 여기서 끝난다.
 *
 * ## 화면이 지키는 것
 * - **2단계는 되는 역할에서만 보인다.** 고르게 해 두고 나중에 막으면 운영자는
 *   「분명 켰는데 안 된다」만 겪는다(판정은 shared `allowedLevels`)
 * - **승인 안 한 문답은 대기함에 따로** 둔다 — 목록에 섞으면 무엇이 나가는지 알 수 없다
 * - 2단계를 고르면 **어른 확인 뒤에만 열린다**고 적는다 — 운영자가 「켰는데 왜 입력칸이 없지」를 겪지 않게
 */

const LEVEL_LABEL: Record<NpcLevel, string> = {
  0: "0 · 버튼만",
  1: "1 · 승인된 문답",
  2: "2 · 자유 대화",
};
const LEVEL_HINT: Record<NpcLevel, string> = {
  0: "정해진 안내만 해요. AI가 끼지 않아 틀린 말을 할 수 없어요",
  1: "AI가 미리 만든 답 중 사장님이 ✅ 한 것만. 서버 없이 동작해요",
  2: "그때그때 AI가 답해요. 어른 확인(셈 문제)을 지난 뒤에만 열려요",
};

export function NpcAgentPanel({ npc, label }: { npc: string; label: string }) {
  const [doc, setDoc] = useState<NpcAgentDoc>({ agents: [] });
  const [kb, setKb] = useState<KbPackDoc>({ packs: [] });
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  /** 쓸 수 있는 AI 공급자 — 하나도 없으면 초안 버튼 자체를 숨긴다(눌러도 되는 게 없다). */
  const [providers, setProviders] = useState<AiProviderOption[]>([]);
  const [provider, setProvider] = useState<AiProvider | "">("");
  const [count, setCount] = useState(6);
  const [hint, setHint] = useState("");
  const [drafting, setDrafting] = useState(false);
  /** 지금 보고 있는 말 — 목록을 이 말로 추린다(원문과 번역을 섞으면 무엇이 나가는지 모른다). */
  const [viewLang, setViewLang] = useState<Lang>("ko");

  useEffect(() => {
    void aiProviders()
      .then((ps) => {
        setProviders(ps);
        if (ps[0]) setProvider(ps[0].id); // 빈 값이면 select 가 아무것도 안 고른 상태로 보인다
      })
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    let alive = true;
    void Promise.all([getNpcAgents(), getAiKb().catch(() => ({ packs: [] }) as KbPackDoc)])
      .then(([a, k]) => {
        if (!alive) return;
        setDoc(a);
        setKb(k);
        setLoaded(true);
      })
      .catch((e: Error) => alive && setMsg(`⚠️ 불러오지 못했어요 (${e.message})`));
    return () => {
      alive = false;
    };
  }, []);

  async function onSave() {
    const r = await saveNpcAgents(doc);
    setMsg(`✅ 저장했어요 — 열린 친구 ${r.open}명 · 검수 대기 ${r.pending}개`);
  }

  useRegisterSave({
    id: "npcagent",
    label: "친구가 아는 것",
    save: onSave,
    draft: { data: () => doc, restore: (d) => setDoc(d as NpcAgentDoc) },
  });

  /** 이 친구의 설정 — 없으면 기본값(책방 사서 · 버튼만). */
  const cur: NpcAgent = agentFor(doc, npc) ?? {
    npc,
    role: "librarian",
    level: 0,
    provider: "anthropic",
    packs: [],
    qa: [],
  };

  const patch = (p: Partial<NpcAgent>) =>
    setDoc((d) => {
      const next = { ...cur, ...p };
      // 역할을 바꿔서 2단계를 못 쓰게 되면 **1로 내린다** — 저장은 됐는데 안 열리는 상태를 안 만든다
      if (!allowedLevels(next.role).includes(next.level)) next.level = 1;
      const has = d.agents.some((a) => a.npc === npc);
      return {
        agents: has ? d.agents.map((a) => (a.npc === npc ? next : a)) : [...d.agents, next],
      };
    });

  const setQaStatus = (id: string, status: "approved" | "rejected" | "pending") =>
    patch({ qa: cur.qa.map((q) => (q.id === id ? { ...q, status } : q)) });

  const removeQa = (id: string) => patch({ qa: cur.qa.filter((q) => q.id !== id) });

  const addQa = () =>
    patch({
      qa: [
        ...cur.qa,
        {
          id: `m_${Date.now().toString(36)}`,
          q: "",
          a: "",
          // 직접 쓴 건 바로 승인 상태다 — 자기가 쓴 걸 자기가 다시 승인하게 하지 않는다.
          status: "approved" as const,
          source: "manual" as const,
        },
      ].slice(0, NPC_AGENT_LIMITS.qa),
    });

  /**
   * 🤖 초안 만들기 — 고른 자료를 읽고 문답을 뽑아 **대기함에** 넣는다.
   *
   * 자료 내용은 안 보낸다(묶음 id 만). 돌아온 것은 전부 검수 대기라
   * 사장님이 ✅ 하기 전에는 아이에게 안 나간다.
   */
  async function onDraft() {
    if (drafting) return;
    if (cur.packs.length === 0) {
      setMsg("⚠️ 먼저 볼 자료를 골라 주세요.");
      return;
    }
    setDrafting(true);
    setMsg("🤖 자료를 읽고 있어요…");
    try {
      const r = await draftNpcQa({
        role: cur.role,
        packs: cur.packs,
        count,
        npcLabel: label,
        hint: hint.trim() || undefined,
        provider: provider || undefined,
        existingIds: cur.qa.map((q) => q.id),
      });
      patch({ qa: [...cur.qa, ...r.qa].slice(0, NPC_AGENT_LIMITS.qa) });
      setMsg(
        r.dropped > 0
          ? `🤖 ${r.qa.length}개를 만들었어요(${r.dropped}개는 아이에게 맞지 않아 버렸어요) · ${r.providerLabel} · 오늘 ${r.remaining}번 남음`
          : `🤖 ${r.qa.length}개를 만들었어요 · ${r.providerLabel} · 오늘 ${r.remaining}번 남음`,
      );
    } catch (e) {
      // 실패 원인을 그대로 — 어느 공급자가 왜 실패했는지 알아야 다른 걸 고른다.
      setMsg(`⚠️ 초안을 만들지 못했어요 — ${(e as Error).message}`);
    } finally {
      setDrafting(false);
    }
  }

  /** 🌏 승인된 한국어 문답을 다른 말로 — 옮긴 것도 전부 검수 대기로 들어온다. */
  async function onTranslate(to: Lang) {
    if (drafting) return;
    setDrafting(true);
    setMsg(`🌏 ${LANG_NAME[to]}(으)로 옮기는 중…`);
    try {
      const r = await translateNpcQa({ npc, lang: to, provider: provider || undefined });
      patch({ qa: [...cur.qa, ...r.qa] });
      setViewLang(to);
      const skipped = r.skipped > 0 ? ` (이미 있는 ${r.skipped}개는 건너뜀)` : "";
      setMsg(
        r.qa.length > 0
          ? `🌏 ${r.qa.length}개를 옮겼어요${skipped} · ${r.providerLabel} — 검수 대기에 있어요`
          : `옮길 것이 없었어요${skipped}`,
      );
    } catch (e) {
      setMsg(`⚠️ 옮기지 못했어요 — ${(e as Error).message}`);
    } finally {
      setDrafting(false);
    }
  }

  const patchQa = (id: string, p: { q?: string; a?: string }) =>
    patch({ qa: cur.qa.map((q) => (q.id === id ? { ...q, ...p } : q)) });

  if (!loaded && !msg) return <p className="hint">불러오는 중…</p>;

  const meta = NPC_ROLE_META[cur.role];
  const levels = allowedLevels(cur.role);
  const docentProviders = providers.filter((p) => (DOCENT_PROVIDERS as readonly string[]).includes(p.id));
  const inLang = (q: { lang?: Lang }) => (q.lang ?? "ko") === viewLang;
  const approved = cur.qa.filter((q) => q.status === "approved" && inLang(q));
  const pending = cur.qa.filter((q) => q.status === "pending" && inLang(q));
  /** 이미 만들어 둔 말들 — 탭으로 띄운다(한국어는 늘 있다). */
  const haveLangs: Lang[] = (["ko", "en", "ja", "zh"] as Lang[]).filter(
    (l) => l === "ko" || cur.qa.some((q) => q.lang === l),
  );
  const packs = packSummary(kb);

  return (
    <div className="agent">
      <Msg text={msg} />

      <h4 className="agent__h">
        <b>{label}</b> 는 무엇을 하는 친구인가요?
      </h4>
      {/* 같은 친구가 여러 마을에 서 있다(병아리는 로비·책방·경주·행사에). 설정은 친구당 하나라
          어느 마을에서 눌러도 같은 말을 한다 — 안 적으면 「경주용으로 맞췄는데 책방에서도
          첨성대 얘기를 한다」가 된다. */}
      <p className="hint">
        이 설정은 <b>모든 마을의 {label}</b> 에게 적용돼요. 마을마다 다르게 하고 싶으시면
        말씀해 주세요.
      </p>
      <div className="agent__roles">
        {NPC_ROLES.map((r) => (
          <button
            key={r}
            type="button"
            className="agent__role"
            data-on={cur.role === r || undefined}
            onClick={() => patch({ role: r })}
          >
            <b>{NPC_ROLE_META[r].label}</b>
            <small>{NPC_ROLE_META[r].hint}</small>
          </button>
        ))}
      </div>

      <h4 className="agent__h">어디까지 말하게 할까요?</h4>
      <div className="agent__levels">
        {levels.map((lv) => (
          <button
            key={lv}
            type="button"
            className="agent__level"
            data-on={cur.level === lv || undefined}
            onClick={() => patch({ level: lv })}
          >
            <b>{LEVEL_LABEL[lv]}</b>
            <small>{LEVEL_HINT[lv]}</small>
          </button>
        ))}
      </div>
      {!meta.freeChatOk ? (
        <p className="hint">
          「{meta.label}」 는 <b>아이가 보는 자리</b>라 자유 대화를 안 열어요. 어른용 역할
          (보호자 상담·다국어 안내)에서는 고를 수 있어요.
        </p>
      ) : null}
      {cur.level === 2 ? (
        <>
          <h4 className="agent__h">자유 대화에 어떤 AI를 쓸까요?</h4>
          <div className="agent__levels">
            {docentProviders.map((p) => (
              <button
                key={p.id}
                type="button"
                className="agent__level"
                data-on={cur.provider === p.id || undefined}
                onClick={() => patch({ provider: p.id as NpcAgent["provider"] })}
              >
                <b>{p.label}</b>
                <small>이 AI 키로 보호자·선생님의 질문에 답해요</small>
              </button>
            ))}
          </div>
          {docentProviders.length === 0 ? (
            <p className="hint">[🔑 AI 키]에서 Claude·GPT·Gemini 중 하나의 키를 넣어 주세요.</p>
          ) : !docentProviders.some((p) => p.id === cur.provider) ? (
            <p className="hint">지금 고른 AI의 키가 없어요. 위에서 사용할 AI를 다시 골라 주세요.</p>
          ) : null}
          <p className="hint">
            💬 자유 대화는 <b>어른 확인을 지난 뒤에만</b> 열려요(간단한 셈 문제). 0~7세는 글을 읽지도
            타이핑도 못 하니, 이 칸의 실제 사용자는 <b>옆에 있는 보호자·선생님</b>이에요.
            <br />
            고른 AI 키가 없거나 그날 대화가 너무 많으면 <b>승인된 문답</b>으로 자동으로 돌아가요.
          </p>
          {/* 🗒️ 들어온 질문(30일) → 승인 문답 초안으로(FR-NPC-05) — 대기함에 넣을 뿐, 게시는 저장 바 */}
          <NpcChatLogBox
            npc={npc}
            qa={cur.qa}
            onDraft={(d) => patch({ qa: [...cur.qa, d].slice(0, NPC_AGENT_LIMITS.qa) })}
          />
        </>
      ) : null}

      {cur.level >= 1 ? (
        <>
          <h4 className="agent__h">어떤 자료를 보고 답할까요?</h4>
          {packs.length === 0 ? (
            <p className="hint">
              아직 자료가 없어요 — [📚 AI 자료]에서 글을 올리면 여기서 고를 수 있어요.
            </p>
          ) : (
            <div className="agent__packs">
              {packs.map((p) => {
                const on = cur.packs.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="agent__pack"
                    data-on={on || undefined}
                    onClick={() =>
                      patch({
                        packs: on ? cur.packs.filter((x) => x !== p.id) : [...cur.packs, p.id],
                      })
                    }
                  >
                    {on ? "✓ " : ""}
                    {p.name} <small>{KB_TAG_LABEL[p.tag]} · 조각 {p.docs}</small>
                  </button>
                );
              })}
            </div>
          )}

          {providers.length > 0 ? (
            <div className="agent__draft">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void onDraft()}
                disabled={drafting || cur.packs.length === 0}
                title={cur.packs.length === 0 ? "먼저 볼 자료를 골라 주세요" : undefined}
              >
                {drafting ? "만드는 중…" : "🤖 AI로 문답 만들기"}
              </button>
              {/* 🔴 「3개/6개/10개」만 떠 있고 **무엇이 3개인지** 아무 데도 없었다. */}
              <select
                aria-label="한 번에 만들 문답 수"
                title="한 번에 만들 문답 수"
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
              >
                {[3, 6, 10].map((n) => (
                  <option key={n} value={n}>
                    {n}개
                  </option>
                ))}
              </select>
              {providers.length > 1 ? (
                <select
                  aria-label="어느 AI로 만들까"
                  title="어느 AI로 만들까"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as AiProvider)}
                >
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              ) : null}
              <input
                className="agent__hint"
                value={hint}
                onChange={(e) => setHint(e.target.value.slice(0, 200))}
                placeholder="원하는 게 있으면 적어 주세요 (예: 아이가 좋아할 만한 것 위주)"
              />
            </div>
          ) : (
            <p className="hint">
              AI 키가 없어서 초안은 못 만들어요 — [🔑 AI 키]에서 넣으면 여기에 버튼이 생겨요.
              키가 없어도 아래 [＋ 직접 쓰기]로 손수 적을 수 있어요.
            </p>
          )}
          <p className="hint">
            만든 문답은 <b>전부 검수 대기</b>로 들어가요. ✅ 를 누른 것만 아이에게 나가요.
          </p>

          {/* 👀 저장하고 게시하고 마을을 걸어가 보기 전에 **여기서** 확인한다.
              무엇이 보일지는 웹과 **같은 함수**가 정한다(qaFor·npcChatText). */}
          <NpcChatPreview agent={cur} label={label} lang={viewLang} />

          {/* 🌏 말 탭 — 원문과 번역을 섞어 보여 주면 무엇이 나가는지 알 수 없다. */}
          <div className="agent__langs">
            {haveLangs.map((l) => (
              <button
                key={l}
                type="button"
                className="btn btn--sm"
                data-on={l === viewLang || undefined}
                onClick={() => setViewLang(l)}
              >
                {LANG_NAME[l]}
                {l === "ko" ? " (원문)" : ""}
              </button>
            ))}
            {providers.length > 0
              ? (["en", "ja", "zh"] as Lang[])
                  .filter((l) => !haveLangs.includes(l))
                  .map((l) => (
                    <button
                      key={l}
                      type="button"
                      className="btn btn--sm"
                      disabled={drafting}
                      onClick={() => void onTranslate(l)}
                    >
                      ＋ {LANG_NAME[l]}(으)로
                    </button>
                  ))
              : null}
          </div>
          {viewLang !== "ko" ? (
            <p className="hint">
              옮긴 문답도 <b>따로 ✅</b> 해야 나가요. 못 읽는 말이면 거절하는 게 맞아요 —
              검수 안 한 문장이 아이에게 가면 안 되니까요.
            </p>
          ) : null}

          {pending.length > 0 ? (
            <>
              <h4 className="agent__h">
                검수 대기 <span className="agent__badge">{pending.length}</span>
              </h4>
              <p className="hint">✅ 를 누른 것만 아이에게 나가요.</p>
              <ul className="agent__qalist">
                {pending.map((q) => (
                  <li key={q.id} className="agent__qa" data-status="pending">
                    <div className="agent__qatext">
                      <b>{q.q}</b>
                      <span>{q.a}</span>
                    </div>
                    <div className="agent__qaact">
                      <button type="button" className="btn btn--sm btn--primary" onClick={() => setQaStatus(q.id, "approved")}>
                        ✅ 승인
                      </button>
                      <button type="button" className="btn btn--sm" onClick={() => setQaStatus(q.id, "rejected")}>
                        ✖️ 거절
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <h4 className="agent__h">
            아이에게 나가는 문답 <span className="agent__badge">{approved.length}</span>
          </h4>
          {approved.length === 0 ? (
            <p className="hint">
              아직 없어요. 아래 [＋ 직접 쓰기]로 하나 적거나, 자료를 고른 뒤 AI 초안을 만들어 보세요.
            </p>
          ) : (
            <p className="hint">
              마을에서 이 친구를 누르면 앞의 <b>{NPC_AGENT_LIMITS.shown}개</b>까지 버튼으로 보여요.
            </p>
          )}
          <ul className="agent__qalist">
            {approved.map((q) => (
              <li key={q.id} className="agent__qa">
                <div className="agent__qatext">
                  <input
                    value={q.q}
                    onChange={(e) => patchQa(q.id, { q: e.target.value.slice(0, NPC_AGENT_LIMITS.q) })}
                    placeholder="아이가 누를 질문"
                  />
                  <textarea
                    value={q.a}
                    rows={2}
                    onChange={(e) => patchQa(q.id, { a: e.target.value.slice(0, NPC_AGENT_LIMITS.a) })}
                    placeholder="그 질문에 나갈 답"
                  />
                </div>
                <div className="agent__qaact">
                  {q.source === "ai" ? <span className="agent__src">AI 초안</span> : null}
                  <button
                    type="button"
                    className="btn btn--sm btn--danger"
                    // 🔴 그림만 있는 단추는 이름이 없다 — 읽어 주는 기기에는 「버튼」으로만 들린다.
                    //    게다가 이건 되돌릴 수 없는 지우기다. 무엇을 지우는지 말해 준다.
                    aria-label="이 문답 지우기"
                    title="이 문답 지우기"
                    onClick={() => removeQa(q.id)}
                  >
                    🗑️
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {/* 🔴 한도에 닿으면 회색 단추만 남았다 — 이유를 적는다(굿즈 화면과 같은 관례). */}
          {cur.qa.length >= NPC_AGENT_LIMITS.qa ? (
            <p className="hint">문답을 {NPC_AGENT_LIMITS.qa}개까지 넣을 수 있어요. 안 쓰는 문답을 지우면 더 넣을 수 있어요.</p>
          ) : (
            <button type="button" className="btn btn--sm" onClick={addQa}>
              ＋ 직접 쓰기
            </button>
          )}
        </>
      ) : (
        <p className="hint">
          지금은 <b>버튼만</b> 나와요. 1단계로 올리면 자료를 고르고 문답을 만들 수 있어요.
        </p>
      )}
    </div>
  );
}
